import { type Cue, Severity } from "hooks/use-cues";

import styles from "./index.module.css";
import { CloseButton } from "components/close-button";
import { useEffect } from "react";

const BASE_Z_INDEX = 1000;

const severtiyToColorMap = {
  [Severity.LOW]: {
    foreground: "var(--color-blue-500)",
    background: "var(--color-blue-50)",
  },
  [Severity.MEDIUM]: {
    foreground: "var(--color-warning-600)",
    background: "var(--color-orange-100)",
  },
  [Severity.HIGH]: {
    foreground: "var(--color-pink-900)",
    background: "var(--color-pink-50)",
  },
  [Severity.CRITICAL]: {
    foreground: "var(--color-error-700)",
    background: "var(--color-error-50)",
  },
};

export const Cues = ({
  pendingCues,
  onAcknowledge,
}: {
  pendingCues: Cue[];
  onAcknowledge: (cue: Cue) => void;
}) => {
  const latestAlert = pendingCues.at(-1);

  useEffect(() => {
    if (!latestAlert || !pendingCues.length) return;

    if (latestAlert.severity !== Severity.LOW) return;

    const timer = setTimeout(() => {
      onAcknowledge(latestAlert);
    }, 4000);

    return () => clearTimeout(timer);
  }, [latestAlert, onAcknowledge, pendingCues.length]);

  if (!pendingCues.length) {
    return null;
  }

  if (!latestAlert) {
    return null;
  }

  const { foreground, background } = severtiyToColorMap[latestAlert.severity];
  const hasDescription = latestAlert.description && latestAlert.description.length > 0;
  const hasDetail = latestAlert.detail && latestAlert.detail.length > 0;

  return (
    <div
      className={styles.cue}
      key={latestAlert.id}
      role="alert"
      style={{ zIndex: BASE_Z_INDEX, backgroundColor: background }}
    >
      <h2 className={styles.title} style={{ color: foreground }}>
        {latestAlert.name}
      </h2>
      <p className={styles.description}>
        {hasDescription ? latestAlert.description : latestAlert.name}
      </p>
      {hasDetail && <p className={styles.detail}>{latestAlert.detail}</p>}
      {latestAlert.severity !== Severity.LOW && (
        <button
          className={styles.button}
          onClick={() => onAcknowledge(latestAlert)}
          style={{ color: foreground, borderColor: foreground }}
          type="button"
        >
          I Acknowledge
        </button>
      )}
      <div className={styles.close}>
        <CloseButton onClick={() => onAcknowledge(latestAlert)} />
      </div>
    </div>
  );
};
